import { SearchItem } from '@yiluhub/yilu-amp-types';
import { YILU_FEATURE } from 'applicationConstants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { yiluTracking } from 'utils/index';

import { useIsFeatureFlagEnabled } from 'context/EnterprisePartnerConfiguration';
import { usePaymentInputContext } from 'context/PaymentInput';
import { PaymentMethods } from 'context/PaymentInput/context';

import { EurailProductSummaryProps } from 'modules/eurail/components/EurailProductSummary/types';
import { getEurailProductDetails } from 'modules/eurail/utils/getProductDetails';

export function useEurailProductSummaryProps(
  currentProduct: SearchItem | undefined,
  paymentFormRef: React.RefObject<HTMLFormElement>,
) {
  const { t } = useTranslation();

  const isCreditCardDisabled = useIsFeatureFlagEnabled(YILU_FEATURE.DISABLE_CREDIT_CARD_PAYMENTS);
  const { currentPaymentMethod } = usePaymentInputContext();
  const isCreditCardPaymentMethod = currentPaymentMethod === PaymentMethods.CREDIT_CARD;

  const eurailProductSummaryProps = useMemo(() => {
    if (!currentProduct) {
      return null;
    }

    const productDetails = getEurailProductDetails(currentProduct);

    function getProviderName(serviceProvider?: string): string {
      if (!serviceProvider) return 'Eurail';
      return serviceProvider.charAt(0).toUpperCase();
    }

    const _props: EurailProductSummaryProps = {
      price: productDetails.price,
      currency: productDetails.currency,
      pass: productDetails.pass,
      numberOfTravelDays: productDetails.numberOfTravelDays,
      validityPeriodMonth: productDetails.validityPeriodMonth,
      adult: productDetails.numberOfAdults,
      youth: productDetails.numberOfYouths,
      child: productDetails.numberOfChildren,
      senior: productDetails.numberOfSeniors,
      fareClass: productDetails.fareClass,
      country: productDetails.to,
      termsDetails: {
        termsUrl: 'https://www.eurail.com/en/terms-conditions/website-terms-of-use',
        productDetails: {
          providerId: getProviderName(productDetails.serviceProvider),
          locationName: getProviderName(productDetails.serviceProvider),
        },
      },
      onClick() {
        if (paymentFormRef?.current) {
          const paymentForm = paymentFormRef.current;
          paymentForm.dispatchEvent(
            new Event('submit', {
              cancelable: true,
              bubbles: true,
            }),
          );

          yiluTracking.sendGAEvent({
            event: 'pip_cta_clicked',
            category: 'eurail-pip',
            label: 'eurail_pip_cta_clicked',
          });
        }
      },
      isDisabled: isCreditCardDisabled && isCreditCardPaymentMethod,
      submitBtnLabel: t('Pay now'),
    };
    return _props;
  }, [paymentFormRef, currentProduct, t, isCreditCardDisabled, isCreditCardPaymentMethod]);

  return eurailProductSummaryProps;
}
